import { useLayoutEffect, useRef, useState } from 'react';
import { SearchInput } from '@reflex/ui/components/shared/SearchInput';
import { Popover, PopoverAnchor, PopoverContent } from '@reflex/ui/components/ui/popover';
import { cn } from '@reflex/ui/lib/utils';
import { FilterMentionMenu, type FilterMentionHandle } from './FilterMentionMenu';
import { detectFilterMention, isBareScope, type FilterMentionToken } from './filter-mention';
import type { FilterDefinition } from './filter-types';

interface FilterSearchInputProps {
  /** Facets reachable from the `@` menu. */
  filters: FilterDefinition[];
  /** The committed search text. Never contains an in-progress `@` token. */
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  'aria-label': string;
  /** Called after `@` commits a facet value, so the bar can reveal the chip. */
  onFilterApplied?: () => void;
  className?: string;
}

/**
 * The list's search box, with `@` as a keyboard path into the filters.
 *
 * Typing `@` opens the facet menu under the caret; picking a facet rewrites the
 * token to `@owner:` and the same box keeps narrowing, now over that facet's
 * values; picking a value applies the filter and the token disappears. It is
 * the composer's `@` mention pointed at filters instead of agent runs, so the
 * two read as the same gesture, and it puts every facet a keystroke away.
 *
 * The token is deliberately *not* part of the committed search text: half of
 * `@owner` is not something anyone wants to search runs for, and letting it
 * through would empty the list under the menu on every keystroke. The box shows
 * the search text with the token spliced back in, and the token only becomes
 * text if it is abandoned (Escape, or typing past it).
 */
export function FilterSearchInput({
  filters,
  value,
  onValueChange,
  placeholder,
  'aria-label': ariaLabel,
  onFilterApplied,
  className,
}: FilterSearchInputProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<FilterMentionHandle>(null);
  const [mention, setMention] = useState<FilterMentionToken | null>(null);
  const [focused, setFocused] = useState(false);
  // Start offset of a token the reader has dismissed, so Escape can hand the
  // text back without the next keystroke popping the menu straight open again.
  const dismissedStart = useRef<number | null>(null);
  // Set alongside a rewrite and applied once the new text is on screen: the
  // input is controlled, so an unrestored caret snaps to the end of the value.
  const pendingCaret = useRef<number | null>(null);

  const displayValue = mention
    ? `${value.slice(0, mention.start)}@${mention.query}${value.slice(mention.start)}`
    : value;

  // No dependency list on purpose: a rewrite that happens to leave the text
  // byte-identical (abandoning a token, say) still needs the caret restored,
  // and a pending caret applied one render late lands in the wrong place.
  useLayoutEffect(() => {
    const caret = pendingCaret.current;
    if (caret === null) return;
    pendingCaret.current = null;
    inputRef.current?.setSelectionRange(caret, caret);
  });

  /** Replace everything after the `@` and keep the menu open. */
  const rewriteQuery = (nextQuery: string) => {
    if (!mention) return;
    pendingCaret.current = mention.start + 1 + nextQuery.length;
    setMention({ start: mention.start, query: nextQuery });
    inputRef.current?.focus();
  };

  /** Give up on the token and leave what was typed behind as plain search text. */
  const abandonToken = (options: { dismiss: boolean }) => {
    if (!mention) return;
    if (options.dismiss) dismissedStart.current = mention.start;
    // The displayed text does not move here, only its meaning: what was a
    // token is now search text, so the caret is already where it belongs.
    setMention(null);
    onValueChange(displayValue);
  };

  const handleChange = (nextDisplay: string) => {
    // Only a keystroke can open the menu: `onChange` does not fire when the
    // caret merely moves through an `@` that was already there.
    const caret = inputRef.current?.selectionStart ?? nextDisplay.length;
    const token = detectFilterMention(nextDisplay, caret);

    if (token && token.start !== dismissedStart.current) {
      setMention(token);
      const end = token.start + 1 + token.query.length;
      onValueChange(`${nextDisplay.slice(0, token.start)}${nextDisplay.slice(end)}`);
      return;
    }

    if (!token) dismissedStart.current = null;
    setMention(null);
    onValueChange(nextDisplay);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!mention) return;
    const menu = menuRef.current;
    if (!menu) return;

    if (event.key === 'ArrowDown') {
      menu.moveDown();
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      menu.moveUp();
      event.preventDefault();
    } else if ((event.key === 'Enter' || event.key === 'Tab') && menu.selectHighlighted()) {
      event.preventDefault();
    } else if (event.key === 'Escape') {
      // Escape ladders out of a scoped facet first; at the root it hands the
      // token back as text rather than deleting what was typed.
      if (!menu.escape()) abandonToken({ dismiss: true });
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === 'Backspace' && isBareScope(mention.query)) {
      // Deleting the `:` of `@owner:` goes back to the facet list rather than
      // leaving a token naming a facet nobody can see.
      rewriteQuery('');
      event.preventDefault();
    }
  };

  return (
    <Popover
      open={mention !== null}
      onOpenChange={(open) => {
        if (!open) abandonToken({ dismiss: true });
      }}
    >
      <PopoverAnchor asChild>
        <div ref={anchorRef} className={cn('relative w-full', className)}>
          <SearchInput
            ref={inputRef}
            size="sm"
            containerClassName="w-full flex-none"
            value={displayValue}
            onValueChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              abandonToken({ dismiss: false });
            }}
            placeholder={placeholder}
            aria-label={ariaLabel}
          />
          {/* Discoverability without permanent chrome: the hint only shows on an
              empty focused box, where there is room and nothing else to read. */}
          {focused && displayValue === '' && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-3xs text-muted-foreground/70"
            >
              <kbd className="rounded-sm border border-border/60 px-1 py-px font-sans">@</kbd> to
              filter
            </span>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-64 p-0"
        // The caret has to stay in the search box: it is still the thing being
        // typed into, and taking focus would end the query mid-word.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        // Clicking a row must not blur the search box: the blur would abandon
        // the token before the click landed, so mouse picks would never apply.
        onMouseDown={(event) => event.preventDefault()}
        // Escape is the input's to handle: it ladders out of a scoped facet
        // before it closes anything, and Radix's own handler runs first (it
        // listens in the capture phase) and would close out from under it.
        onEscapeKeyDown={(event) => event.preventDefault()}
        // Clicking into the search box is not "outside": the box is what the
        // menu is completing, and dismissing there would drop the token the
        // moment someone reached for the caret.
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
        }}
      >
        <FilterMentionMenu
          ref={menuRef}
          filters={filters}
          query={mention?.query ?? ''}
          onApply={(definition, optionValue) => {
            definition.onValueChange(optionValue);
            onFilterApplied?.();
            // The token was never part of the search text, so applying it is
            // just dropping it: what is left in the box is what was there.
            pendingCaret.current = mention?.start ?? null;
            setMention(null);
            inputRef.current?.focus();
          }}
          onScopeFacet={(facetKey) => rewriteQuery(`${facetKey}:`)}
          onResetQuery={() => rewriteQuery('')}
        />
      </PopoverContent>
    </Popover>
  );
}
